/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and cannot be distributed/used outside of Vlinder Labs Pvt. Ltd without explicit written consent.
 */

import { IAgentPlugin, IPluginMethodMap } from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import { VApplication } from "../../../application";
import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_MY_BANK_DETAIL } from "../graphql/getMyBankDetail.query";
import { toHeaderCase } from "js-convert-case";
import { StorageModule } from "@vlinder-web/storage-module-react";
import { AppNavigatorService } from "../../appNavigatorService";
import moment from "moment";
import jwt_decode from "jwt-decode";

const getWhoamiSrv = (): AppNavigatorService | undefined => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const getGraphQLModule = (): GraphqlServerModule | undefined => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;

  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    if (!graphqlModule) return {};

    const client = graphqlModule.getClient("graphql-gateway");
    const storageSrv = StorageModule.getInstance();
    const vuid = jwt_decode<{ vuid: string }>(getWhoamiSrv()?.getToken() || "");

    try {
      if (!client) return {};

      const res = await client.query({
        query: GET_MY_BANK_DETAIL,
        variables: {
          input: { vuid: vuid?.vuid },
          input1: {
            where: [
              { column: "performedBy", op: "Equal", value: [vuid?.vuid] },
              { column: "type", op: "In", value: ["BANK_DETAIL"] },
            ],
          },
        },
        fetchPolicy: "network-only",
      });

      console.log("Hi" + "kishore")

      const bankDetails = res?.data?.getBankAccountDetail ?? [];
      const firstBank = bankDetails[0] ?? null;
      const txnList = res?.data?.getTxnList?.data ?? [];

      return {
        title: config?.title,
        listItems: this.getListItems(firstBank),
        buttons: firstBank
          ? config?.buttons?.filter((b) => b.key === "edit" || b.key === "saveBank")
          : config?.buttons?.filter((b) => b.key === "saveBank"),
        txList: res?.data?.getTxnList,
        timelineData: this.getTimeLine(txnList),
        carouselItems: firstBank ? this.getCarouselItems(firstBank?.chequeLeaf) : [],
        bankAccordionItems: this.getBankAccordion(bankDetails),
        initialData: firstBank
          ? {
            accId: firstBank._id || "",
            accHolderName: firstBank.accHolderName || "",
            bankName: firstBank.bankName || "",
            accountNo: firstBank.accountNo || "",
            type: firstBank.type || "",
            swiftCode: firstBank.swiftCode || "",
            checkLeafId: firstBank.chequeLeaf?._id || "",
            chequeLeaf: firstBank.chequeLeaf
              ? [{ base64: firstBank.chequeLeaf.doc || "", ...firstBank.chequeLeaf }]
              : [],
          }
          : {
            accId: firstBank?._id || "",
            accHolderName: firstBank?.accHolderName || "",
            bankName: firstBank?.bankName || "",
            accountNo: firstBank?.accountNo || "",
            type: firstBank?.type || "",
            swiftCode: firstBank?.swiftCode || "",
            checkLeafId: firstBank?.chequeLeaf?._id || "",
            chequeLeaf: [],
          },

      };
    } catch (err: any) {
      console.log("Sign In user error", err);
      throw new Error(err?.response?.data?.error?.display_message || "Unknown error");
    }
  }

  private getCarouselItems(item: any) {
    if (!item) return [];
    return [
      {
        ...item,
        type: this.getDocType(item?.fileType),
        src: item?.doc,
        title: item?.name,
        poster: item?.poster,
      },
    ];
  }

  private getBankAccordion(items: any[]) {
    if (!Array.isArray(items)) return [];
    return items.map((ele) => {
      const chequeLeaf = ele?.chequeLeaf;
      return {
        ...ele,
        chequeLeaf: chequeLeaf
          ? {
            ...chequeLeaf,
            type: this.getDocType(chequeLeaf.fileType),
            src: chequeLeaf.doc ?? "",
            title: chequeLeaf.name ?? "",
            poster: chequeLeaf.poster ?? "",
          }
          : null,
      };
    });
  }

  private getListItems(res: any) {
    return config?.listItems?.map((item: any) => {
      switch (item?.title) {
        case "Bank Name":
          return { ...item, subTitle: toHeaderCase(res?.bankName ?? "") };
        case "Account Holder's Name":
          return { ...item, subTitle: res?.accHolderName ?? "-" };
        case "Account Number":
          return { ...item, subTitle: res?.accountNo ?? "-", masked: true };
        case "Account Type":
          return { ...item, subTitle: res?.type ?? "-" };
        case "SWIFT or BIC code":
          return { ...item, subTitle: res?.swiftCode ?? "-" };
        default:
          return item;
      }
    });
  }

  private getDocType(fileType: string | undefined) {
    if (!fileType) return null;
    if (fileType === "application/pdf") return "pdf";
    if (["image/png", "image/jpg", "image/jpeg"].includes(fileType)) return "img";
    return null;
  }

  private getTimeLine(data: any[] = []) {
    return data.sort((a: any, b: any) => parseInt(b?.when || "0") - parseInt(a?.when || "0")).map((element) => ({
      key: element?.id,
      headline: toHeaderCase(element?.action ?? ""),
      headlineBackgroundColor: "#579783",
      children: this.getChildren(element),
    }));
  }

  private getChildren(data: any) {
    return [
      {
        itemId: data?.id,
        multiColList: this.getProvenanceAccordion(data),
        title: toHeaderCase(data?.action ?? ""),
        subtitle: data?.payload?.comment,
      },
    ];
  }

  private getProvenanceAccordion(res: any) {
    if (!res) return { numberOfColumns: "2", data: [] };

    const _list: any[] = [];
    const _location: any[] = [];
    const _date: any[] = [];

    if (res?.performedBy?.firstName) {
      _list.push({
        title: toHeaderCase("by"),
        subTitle: `${toHeaderCase(res.performedBy.firstName)} ${toHeaderCase(res.performedBy.lastName)}`,
        icon: config?.orgIcon,
      });
    }

    if (res?.location) {
      _location.push({
        title: toHeaderCase("at"),
        subTitle: res.location,
        icon: config?.locationIcon,
      });
    }

    if (res?.when) {
      _date.push({
        title: toHeaderCase("on"),
        subTitle: moment(parseInt(res.when)).format("LLL"),
        icon: config?.dateIcon,
      });
    }

    return { numberOfColumns: "2", data: [..._list, ..._location, ..._date] };
  }
}
